import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const STYLES = `
  .asc-wrap { max-width:1100px; margin:0 auto; }
  .asc-cal-grid { display:grid; grid-template-columns:repeat(7,1fr); gap:3px; }
  .asc-cal-day-header { text-align:center; font-size:10px; font-weight:700; color:#B8B5AB; letter-spacing:0.08em; padding:6px 0; }
  .asc-cal-day { min-height:80px; padding:5px; border-radius:8px; background:#F8F7F4; border:1px solid #E8E6DF; font-size:11px; transition:all 0.15s; }
  .asc-cal-day.today { border-color:#C9A84C; background:#FDF6E3; }
  .asc-cal-day.has-jobs { background:#EBF4FF; border-color:#1A3A5E; }
  .asc-cal-day.has-jobs:hover { background:#D6E9FF; box-shadow:0 2px 8px rgba(13,27,62,0.12); cursor:pointer; }
  .asc-cal-day-num { font-weight:600; color:#0D1B3E; font-size:12px; margin-bottom:3px; }
  .asc-cal-day.today .asc-cal-day-num { color:#8A5C00; }
  .asc-event { font-size:9px; padding:2px 5px; border-radius:4px; margin-top:2px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:pointer; transition:opacity 0.15s; }
  .asc-event:hover { opacity:0.8; }
  .asc-event.new                { background:#FDF6E3; color:#8A5C00; }
  .asc-event.awaiting_assignment { background:#FDF6E3; color:#8A5C00; }
  .asc-event.pending_acceptance  { background:#EBF4FF; color:#185FA5; }
  .asc-event.assigned            { background:#EBF4FF; color:#1A3A5E; }
  .asc-event.confirmed           { background:#EAF5EE; color:#1A4A2E; }
  .asc-event.in_progress         { background:#0D1B3E; color:#C9A84C; }
  .asc-event.scanbacks_uploaded  { background:#E8F5F0; color:#1A4A3E; }
  .asc-event.completed           { background:#EAF5EE; color:#2D7A4F; }
  .asc-event.paid                { background:#EAF5EE; color:#2D7A4F; }
  .asc-event.default             { background:#0D1B3E; color:#C9A84C; }
  .asc-legend { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
  .asc-legend-item { display:flex; align-items:center; gap:5px; font-size:10px; color:#7A7870; }
  .asc-legend-dot { width:10px; height:10px; border-radius:2px; flex-shrink:0; }
  .asc-detail { background:#0D1B3E; border-radius:14px; padding:20px; border:1px solid rgba(201,168,76,0.3); margin-top:16px; }
  .asc-detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin:12px 0; }
  @media(max-width:480px){ .asc-detail-grid { grid-template-columns:1fr; } }
  .asc-detail-field { font-size:12px; color:rgba(255,255,255,0.7); }
  .asc-detail-label { font-size:10px; color:rgba(255,255,255,0.4); margin-bottom:2px; letter-spacing:0.06em; }
  .asc-list-item { display:flex; align-items:center; justify-content:space-between; padding:12px 0; border-bottom:1px solid #F0EEE8; gap:12px; cursor:pointer; transition:background 0.15s; flex-wrap:wrap; }
  .asc-list-item:last-child { border-bottom:none; }
  .asc-list-item:hover { background:#F8F7F4; margin:0 -20px; padding:12px 20px; border-radius:8px; }
  .asc-status-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
`

const STATUS_COLORS = {
  new:                 '#C9A84C',
  awaiting_assignment: '#C9A84C',
  pending_acceptance:  '#5BA5E5',
  assigned:            '#3B82F6',
  confirmed:           '#2D7A4F',
  in_progress:         '#C9A84C',
  scanbacks_uploaded:  '#2D7A6A',
  completed:           '#5BA882',
  awaiting_review:     '#8B5CF6',
  paid:                '#5BA882',
  closed:              '#8A8A8A',
  cancelled:           '#E24B4A',
}

const STATUS_LABELS = {
  new:                 'New',
  awaiting_assignment: 'Finding Notary',
  pending_acceptance:  'Notary Notified',
  assigned:            'Assigned',
  confirmed:           'Confirmed',
  in_progress:         'In Progress',
  scanbacks_uploaded:  'Docs Uploaded',
  completed:           'Completed',
  awaiting_review:     'Awaiting Review',
  paid:                'Paid',
  closed:              'Closed',
  cancelled:           'Cancelled',
}

export default function AdminSchedule() {
  const navigate      = useNavigate()
  const [jobs,        setJobs]        = useState([])
  const [loading,     setLoading]     = useState(true)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)
  const [selectedJobs, setSelectedJobs] = useState([])
  const [view,        setView]        = useState('calendar') // 'calendar' | 'list'
  const [filter,      setFilter]      = useState('all')

  useEffect(() => { loadJobs() }, [])

  async function loadJobs() {
    const { data } = await supabase.from('jobs')
      .select('*, notary:profiles!jobs_notary_id_fkey(full_name)')
      .not('scheduled_at', 'is', null)
      .order('scheduled_at', { ascending: true })
    setJobs(data || [])
    setLoading(false)
  }

  const year  = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const firstDay    = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today       = new Date()
  const monthName   = currentDate.toLocaleString('en-US', { month:'long', year:'numeric' })

  // Filter jobs
  const filteredJobs = jobs.filter(j => {
    if (filter === 'all') return true
    if (filter === 'active') return ['new','awaiting_assignment','pending_acceptance','assigned','confirmed','in_progress','scanbacks_uploaded'].includes(j.status)
    if (filter === 'today') {
      const d = new Date(j.scheduled_at)
      return d.toDateString() === today.toDateString()
    }
    if (filter === 'week') {
      const d = new Date(j.scheduled_at)
      const weekEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      return d >= today && d <= weekEnd
    }
    return true
  })

  // Map jobs to calendar days
  const jobsByDay = {}
  filteredJobs.forEach(j => {
    const d = new Date(j.scheduled_at)
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate()
      if (!jobsByDay[day]) jobsByDay[day] = []
      jobsByDay[day].push(j)
    }
  })

  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  function handleDayClick(day) {
    const dayJobs = jobsByDay[day] || []
    if (dayJobs.length === 0) return
    setSelectedDay(day)
    setSelectedJobs(dayJobs)
  }

  function handleJobClick(job) {
    navigate(`/admin/jobs/${job.id}`)
  }

  // Today's and upcoming jobs for list view
  const todayJobs = jobs.filter(j => new Date(j.scheduled_at).toDateString() === today.toDateString())
  const upcomingJobs = jobs.filter(j => {
    const d = new Date(j.scheduled_at)
    return d > today && ['new','awaiting_assignment','pending_acceptance','assigned','confirmed','in_progress'].includes(j.status)
  }).slice(0, 20)

  if (loading) return (
    <div style={{ textAlign:'center', padding:60 }}>
      <div className="spinner" style={{ margin:'auto' }} />
    </div>
  )

  return (
    <>
      <style>{STYLES}</style>
      <div className="asc-wrap">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:10 }}>
          <h1 className="page-title" style={{ margin:0 }}>📅 Signings Calendar</h1>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setView('calendar')}
              style={{ fontSize:12, padding:'7px 14px', borderRadius:20, border:'1.5px solid', cursor:'pointer', fontWeight:view==='calendar'?700:400,
                background:view==='calendar'?'#0D1B3E':'#fff', color:view==='calendar'?'#C9A84C':'#7A7870', borderColor:view==='calendar'?'#0D1B3E':'#E8E6DF' }}>
              📅 Calendar
            </button>
            <button onClick={() => setView('list')}
              style={{ fontSize:12, padding:'7px 14px', borderRadius:20, border:'1.5px solid', cursor:'pointer', fontWeight:view==='list'?700:400,
                background:view==='list'?'#0D1B3E':'#fff', color:view==='list'?'#C9A84C':'#7A7870', borderColor:view==='list'?'#0D1B3E':'#E8E6DF' }}>
              📋 List
            </button>
          </div>
        </div>

        {/* FILTER TABS */}
        <div style={{ display:'flex', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          {[
            { value:'all',    label:'All Signings' },
            { value:'active', label:'🟢 Active'    },
            { value:'today',  label:'📌 Today'     },
            { value:'week',   label:'📅 This Week' },
          ].map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              style={{ fontSize:12, padding:'6px 14px', borderRadius:20, border:'1.5px solid', cursor:'pointer', fontWeight:filter===f.value?700:400,
                background:filter===f.value?'#C9A84C':'#fff', color:filter===f.value?'#0D1B3E':'#7A7870', borderColor:filter===f.value?'#C9A84C':'#E8E6DF' }}>
              {f.label}
            </button>
          ))}
          <span style={{ fontSize:12, color:'#B8B5AB', alignSelf:'center', marginLeft:4 }}>
            {filteredJobs.length} signing{filteredJobs.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── CALENDAR VIEW ── */}
        {view === 'calendar' && (
          <>
            {/* Month navigation */}
            <section className="section-card">
              <div className="section-header">
                <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                  style={{ background:'none', border:'1px solid #E8E6DF', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:14, color:'#0D1B3E' }}>←</button>
                <h2 className="section-title" style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20 }}>{monthName}</h2>
                <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                  style={{ background:'none', border:'1px solid #E8E6DF', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:14, color:'#0D1B3E' }}>→</button>
              </div>
              <div className="section-body">

                {/* Legend */}
                <div className="asc-legend">
                  {[
                    { label:'New / Unassigned', color:'#FDF6E3', border:'#C9A84C' },
                    { label:'Notary Assigned',  color:'#EBF4FF', border:'#3B82F6' },
                    { label:'Confirmed',        color:'#EAF5EE', border:'#2D7A4F' },
                    { label:'In Progress',      color:'#0D1B3E', border:'#C9A84C' },
                    { label:'Completed',        color:'#EAF5EE', border:'#5BA882' },
                  ].map((l, i) => (
                    <div key={i} className="asc-legend-item">
                      <div className="asc-legend-dot" style={{ background:l.color, border:`1px solid ${l.border}` }} />
                      {l.label}
                    </div>
                  ))}
                </div>

                {/* Day headers */}
                <div className="asc-cal-grid" style={{ marginBottom:4 }}>
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <div key={d} className="asc-cal-day-header">{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="asc-cal-grid">
                  {cells.map((day, i) => {
                    if (!day) return <div key={`e${i}`} />
                    const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear()
                    const dayJobs = jobsByDay[day] || []
                    const isSelected = selectedDay === day
                    return (
                      <div key={day}
                        className={`asc-cal-day${isToday ? ' today' : ''}${dayJobs.length > 0 ? ' has-jobs' : ''}`}
                        onClick={() => handleDayClick(day)}
                        style={{ border: isSelected ? '2px solid #C9A84C' : undefined }}>
                        <div className="asc-cal-day-num">{day}</div>
                        {dayJobs.slice(0, 3).map((j, idx) => (
                          <div key={idx}
                            className={`asc-event ${j.status || 'default'}`}
                            onClick={e => { e.stopPropagation(); handleJobClick(j) }}
                            title={`${j.borrower_name} — ${j.signing_type}`}>
                            {j.borrower_name?.split(' ')[0] || j.job_number}
                          </div>
                        ))}
                        {dayJobs.length > 3 && (
                          <div style={{ fontSize:9, color:'#7A7870', marginTop:2, fontWeight:600 }}>+{dayJobs.length - 3} more</div>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Selected day detail */}
                {selectedDay && selectedJobs.length > 0 && (
                  <div className="asc-detail">
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                      <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#C9A84C', fontWeight:600 }}>
                        {new Date(year, month, selectedDay).toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}
                        <span style={{ fontSize:13, color:'rgba(201,168,76,0.6)', marginLeft:8 }}>{selectedJobs.length} signing{selectedJobs.length !== 1 ? 's' : ''}</span>
                      </div>
                      <button onClick={() => { setSelectedDay(null); setSelectedJobs([]) }}
                        style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer', fontSize:18 }}>✕</button>
                    </div>
                    {selectedJobs.map(j => (
                      <div key={j.id}
                        onClick={() => handleJobClick(j)}
                        style={{ background:'rgba(255,255,255,0.06)', borderRadius:10, padding:'12px 14px', marginBottom:8, cursor:'pointer', border:'1px solid rgba(201,168,76,0.15)', transition:'all 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.06)'}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:'#fff', marginBottom:3 }}>{j.borrower_name}</div>
                            <div style={{ fontSize:11, color:'rgba(255,255,255,0.6)' }}>{j.signing_type}</div>
                            <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)', marginTop:2 }}>
                              📍 {j.signing_city}, {j.signing_state}
                              {j.scheduled_at && <span style={{ marginLeft:8 }}>🕐 {new Date(j.scheduled_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}</span>}
                            </div>
                            {j.notary?.full_name && <div style={{ fontSize:11, color:'#C9A84C', marginTop:2 }}>👤 {j.notary.full_name}</div>}
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                            <div style={{ display:'inline-flex', alignItems:'center', gap:4, background:'rgba(0,0,0,0.2)', borderRadius:20, padding:'3px 8px' }}>
                              <div style={{ width:6, height:6, borderRadius:'50%', background:STATUS_COLORS[j.status] || '#C9A84C' }} />
                              <span style={{ fontSize:10, color:'rgba(255,255,255,0.7)', fontWeight:600 }}>{STATUS_LABELS[j.status] || j.status}</span>
                            </div>
                            <div style={{ fontSize:10, color:'rgba(201,168,76,0.6)', fontFamily:'Cormorant Garamond,serif' }}>{j.job_number}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty state */}
                {Object.keys(jobsByDay).length === 0 && (
                  <div style={{ textAlign:'center', padding:'32px 0', color:'#B8B5AB', fontSize:13, marginTop:8 }}>
                    📅 No signings scheduled this month with current filters!!
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {/* ── LIST VIEW ── */}
        {view === 'list' && (
          <>
            {/* TODAY */}
            <section className="section-card" style={{ marginBottom:16 }}>
              <div className="section-header">
                <h2 className="section-title">📌 Today — {today.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</h2>
                <span style={{ fontSize:12, color:'#B8B5AB' }}>{todayJobs.length} signing{todayJobs.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="section-body" style={{ padding:0 }}>
                {todayJobs.length === 0 ? (
                  <div style={{ padding:'24px 20px', color:'#B8B5AB', fontSize:13, textAlign:'center' }}>✅ No signings today — enjoy the calm!!</div>
                ) : todayJobs.map(j => (
                  <div key={j.id} className="asc-list-item" style={{ padding:'12px 20px' }} onClick={() => handleJobClick(j)}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
                      <div className="asc-status-dot" style={{ background:STATUS_COLORS[j.status] || '#C9A84C' }} />
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:'#0D1B3E' }}>{j.borrower_name}</div>
                        <div style={{ fontSize:11, color:'#7A7870' }}>{j.signing_type} · {j.signing_city}, {j.signing_state}</div>
                        {j.notary?.full_name && <div style={{ fontSize:11, color:'#9A7A30' }}>👤 {j.notary.full_name}</div>}
                      </div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#C9A84C' }}>
                        {new Date(j.scheduled_at).toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}
                      </div>
                      <div style={{ fontSize:10, color:'#B8B5AB', marginTop:2 }}>{STATUS_LABELS[j.status] || j.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* UPCOMING */}
            <section className="section-card">
              <div className="section-header">
                <h2 className="section-title">📅 Upcoming Signings</h2>
                <span style={{ fontSize:12, color:'#B8B5AB' }}>{upcomingJobs.length} scheduled</span>
              </div>
              <div className="section-body" style={{ padding:0 }}>
                {upcomingJobs.length === 0 ? (
                  <div style={{ padding:'24px 20px', color:'#B8B5AB', fontSize:13, textAlign:'center' }}>No upcoming signings scheduled!!</div>
                ) : upcomingJobs.map(j => {
                  const d = new Date(j.scheduled_at)
                  return (
                    <div key={j.id} className="asc-list-item" style={{ padding:'12px 20px' }} onClick={() => handleJobClick(j)}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
                        <div className="asc-status-dot" style={{ background:STATUS_COLORS[j.status] || '#C9A84C' }} />
                        <div>
                          <div style={{ fontSize:13, fontWeight:600, color:'#0D1B3E' }}>{j.borrower_name}</div>
                          <div style={{ fontSize:11, color:'#7A7870' }}>{j.signing_type} · {j.signing_city}, {j.signing_state}</div>
                          {j.notary?.full_name && <div style={{ fontSize:11, color:'#9A7A30' }}>👤 {j.notary.full_name}</div>}
                        </div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:12, fontWeight:600, color:'#0D1B3E' }}>
                          {d.toLocaleDateString('en-US', { month:'short', day:'numeric' })}
                        </div>
                        <div style={{ fontSize:11, color:'#C9A84C', marginTop:1 }}>
                          {d.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}
                        </div>
                        <div style={{ fontSize:10, color:'#B8B5AB', marginTop:1 }}>{STATUS_LABELS[j.status] || j.status}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </>
  )
}
